import { HStack, PageContainer, PageHeader, Spread, Text } from '@/components/Atoms'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import { Spinner } from '@/components/Spinner'
import {
  agreementDetailsExportQueryOptions,
  agreementDetailsRawExportQueryOptions,
  agreementDetailsSearchSchema,
  useAgreements,
} from '@/hooks/queries/crt/useAgreement'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryClient } from '@/main'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import AgreementsDataTable from './-components/AgreementsDataTable'
import { ConfigurationMenu } from './-components/ConfigurationMenu'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/insights/agreements/',
)({
  component: RouteComponent,
  validateSearch: (search) => agreementDetailsSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { data, isPending, isError } = useAgreements({
    ...search,
    page: search.page ?? 1,
    pageSize,
  })

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.agreement.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth="wide">
      <Spread>
        <PageHeader>{t('agreement.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvMenu
            items={[
              <ExportCsvMenuItem
                label={t('export.viewData', { ns: 'common' })}
                description={t('export.viewDataDescription', { ns: 'common' })}
                getCsvExport={() =>
                  queryClient.ensureQueryData(agreementDetailsExportQueryOptions(search))
                }
                rowCount={data.rowCount}
              />,
              <ExportCsvMenuItem
                label={t('export.rawData', { ns: 'common' })}
                description={t('export.rawDataDescription', { ns: 'common' })}
                getCsvExport={() =>
                  queryClient.ensureQueryData(agreementDetailsRawExportQueryOptions)
                }
                rowCount={data.rawRowCount}
              />,
            ]}
          />
          <ConfigurationMenu />
        </HStack>
      </Spread>

      <AgreementsDataTable
        data={data.results}
        rowCount={data.rowCount}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />
    </PageContainer>
  )
}
